import React, { useState } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Mail, Loader2, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const BlogSubscribeForm = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | already | error
  const [message, setMessage] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [submittedName, setSubmittedName] = useState('');
  const [resending, setResending] = useState(false);

  const handleSubscribe = async () => {
    if (!email || !name) return;
    setStatus('loading');
    try {
      const res = await fetch(`${API}/api/subscribers/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name }),
      });
      const data = await res.json();
      if (res.ok) {
        setSubmittedEmail(email);
        setSubmittedName(name);
        if (data.already_subscribed) {
          setStatus('already');
          setMessage(data.message);
        } else {
          setStatus('success');
          setMessage(data.message);
        }
        setEmail('');
        setName('');
      } else {
        setStatus('error');
        setMessage(data.detail || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please try again.');
    }
  };

  const handleResend = async () => {
    if (!submittedEmail || !submittedName) return;
    setResending(true);
    try {
      const res = await fetch(`${API}/api/subscribers/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: submittedEmail, name: submittedName }),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage(data.already_subscribed ? data.message : "Confirmation email resent! Please check your inbox.");
      }
    } catch {
      setMessage('Failed to resend. Please try again.');
    }
    setResending(false);
  };

  if (status === 'already') {
    return (
      <div data-testid="subscribe-already" className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center">
        <Mail className="w-8 h-8 text-amber-600 mx-auto mb-3" />
        <p className="text-amber-800 font-semibold text-base">{message}</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div data-testid="subscribe-success" className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center">
        <Mail className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
        <p className="text-emerald-800 font-semibold text-base">{message}</p>
        <p className="text-emerald-600 text-sm mt-1">Click the confirmation link in your email to start receiving blog updates.</p>
        <button
          data-testid="resend-confirmation-btn"
          onClick={handleResend}
          disabled={resending}
          className="mt-3 inline-flex items-center gap-1.5 text-sm text-emerald-700 hover:text-emerald-900 underline underline-offset-2 disabled:opacity-50"
        >
          {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {resending ? 'Sending...' : "Didn't receive it? Resend confirmation"}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="blog-subscribe-form" className="bg-gray-50 border border-gray-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-3">
        <Mail className="w-5 h-5 text-emerald-600" />
        <h3 className="text-base font-semibold text-gray-900">Subscribe to our blog</h3>
      </div>
      <p className="text-sm text-gray-600 mb-4">Get the latest finance insights and tax tips delivered to your inbox.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          data-testid="subscribe-name"
          type="text"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="flex-1"
        />
        <Input
          data-testid="subscribe-email"
          type="email"
          placeholder="Your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1"
        />
        <Button
          data-testid="subscribe-btn"
          onClick={handleSubscribe}
          disabled={!email || !name || status === 'loading'}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 whitespace-nowrap"
        >
          {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Subscribe'}
        </Button>
      </div>
      {status === 'error' && (
        <p data-testid="subscribe-error" className="text-red-600 text-sm mt-2">{message}</p>
      )}
    </div>
  );
};
